/**
 * ReauthorizeDialog (Story 4.14, Task 7)
 *
 * Confirmation dialog before re-authorizing an integration.
 * Shows current account name and initiates OAuth on confirm.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from "@provenleap/ui";

/** Map platform ID to human-readable display name */
const PLATFORM_DISPLAY: Record<string, string> = {
  "google-analytics-4": "Google Analytics 4",
  "google-search-console": "Google Search Console",
  "google-ads": "Google Ads",
  "meta-ads": "Meta Ads",
  "youtube-analytics": "YouTube Analytics",
  "google-business-profile": "Google Business Profile",
  "google-pagespeed": "PageSpeed Insights",
};

interface ReauthorizeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountName: string | null;
  platform: string;
  onConfirm: () => void;
  loading?: boolean;
}

export function ReauthorizeDialog({
  open,
  onOpenChange,
  accountName,
  platform,
  onConfirm,
  loading,
}: ReauthorizeDialogProps) {
  const displayName = PLATFORM_DISPLAY[platform] ?? platform;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Re-authorize Integration</DialogTitle>
          <DialogDescription>
            {accountName
              ? `You are currently connected as "${accountName}". Re-authorizing will refresh this connection.`
              : `Re-authorizing will refresh this ${displayName} connection.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading ? "Connecting..." : "Re-authorize"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
